import { Link } from "@tanstack/react-router";
import { creditsToUsd, formatBytes, formatDate, formatNumber, RUN_KIND_LABELS } from "../../lib/format";
import { Button, Card, EmptyState, ErrorBox, Loading, PageHeader, Stat, StatusBadge, Table, Td } from "../../components/ui";
import { can, useMigrations, useRuns, useWorkspace, useWorkspaceId } from "./hooks";

export function OverviewPage() {
  const id = useWorkspaceId();
  const ws = useWorkspace(id);
  const runs = useRuns(id);
  const migrations = useMigrations(id);
  if (ws.isLoading) return <Loading />;
  if (ws.error) return <ErrorBox error={ws.error} />;
  const d = ws.data!;
  const migName = new Map((migrations.data ?? []).map((m) => [m.id, m.name]));
  return (
    <>
      <PageHeader
        title={d.workspace.name}
        subtitle={`${d.workspace.partner ? `Managed by ${d.workspace.partner.name} · ` : ""}Region ${d.workspace.region.toUpperCase()} · You're ${d.role}`}
        actions={
          can(d.permissions, "migrations.run") && (
            <Link to="/w/$workspaceId/migrations/new" params={{ workspaceId: id }}>
              <Button>New migration</Button>
            </Link>
          )
        }
      />
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Credits" value={d.balance === null ? "—" : formatNumber(d.balance)} hint={d.balance === null ? undefined : creditsToUsd(d.balance)} />
        <Stat label="Migrations" value={d.stats.migrations} />
        <Stat label="Connections" value={d.stats.connections} />
        <Stat label="Stored data" value={formatBytes(d.stats.storageBytes)} />
      </div>
      <Card title="Recent runs">
        {runs.isLoading ? (
          <Loading />
        ) : runs.data?.length ? (
          <Table head={["Migration", "Type", "Status", "Records", "Credits", "Started"]}>
            {runs.data.slice(0, 10).map((r) => (
              <tr key={r.id}>
                <Td>
                  <Link className="text-accent hover:underline" to="/w/$workspaceId/migrations/$migrationId" params={{ workspaceId: id, migrationId: r.migrationId }}>
                    {migName.get(r.migrationId) ?? r.migrationId}
                  </Link>
                </Td>
                <Td>
                  {RUN_KIND_LABELS[r.kind]}
                  {r.preview ? " (preview)" : ""}
                </Td>
                <Td>
                  <StatusBadge status={r.status} />
                </Td>
                <Td className="tabular">{formatNumber((r.summary?.counts?.rowsRead as number) ?? r.summary?.diff?.total ?? 0)}</Td>
                <Td className="tabular">{formatNumber(r.creditsUsed)}</Td>
                <Td className="muted">{formatDate(r.createdAt)}</Td>
              </tr>
            ))}
          </Table>
        ) : (
          <EmptyState title="No runs yet">Create a migration, preview it for free, then run it. Every run can be reverted.</EmptyState>
        )}
      </Card>
    </>
  );
}
